import React, { useContext, useMemo } from "react";
import { routeGroups, RoutePaths } from "../enumStrings";
import { UserTypeContext } from "../contexts/UserTypeContext";
import { useGetUserFromUserIdInAuthState } from "./useGetUserFromUserIdInAuth";
import { ItemsPurchasableByChamberEnum, UserType } from "../graphql/types";
import { Route } from "react-router-dom";
import PageContainer from "../components/Layout/PageContainer";
import {
  filterCmpRouteGroups,
  isCmpRouteAllowed,
} from "../utils/cmpRouteAccess";
import { getCmpExternalRouteHref } from "../utils/cmpExternalRouteHref";
import { getPreferredCmpInternalLandingPath } from "./getPreferredCmpInternalLandingPath";

type NavBarConfig = {
  title: string;
  path: string;
  icon: string;
};

type UserRoutesToShow = React.ReactElement<{
  path: string;
  key: string;
  element: React.ReactNode;
}>;

type FirstAllowedPathThatShouldBeConsideredTheHomePageForUser =
  | string
  | null;

const maybeWrapWithTestingOverlay = (
  path: RoutePaths,
  element: React.ReactNode,
) => {
  // if (testingOverlayExcludedPaths.has(path)) {
  return element;
  // }

  // return <TestingInProgressOverlay>{element}</TestingInProgressOverlay>;
};

export const useGetLoggedInAppInfo = (): {
  navBarConfigToShow: NavBarConfig[] | undefined;
  userRoutesToShow: UserRoutesToShow[] | undefined;
  firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser;
  firstAllowedExternalPathForUser: string | null;
  userInfoLoading: boolean;
  userInfoError: Error | undefined;
} => {
  const {
    state: { userType },
  } = useContext(UserTypeContext);

  const {
    user: userInfo,
    loading: userInfoLoading,
    error: userInfoError,
  } = useGetUserFromUserIdInAuthState();

  const {
    navBarConfigToShow,
    userRoutesToShow,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
    firstAllowedExternalPathForUser,
  } = useMemo((): {
    navBarConfigToShow: NavBarConfig[] | undefined;
    userRoutesToShow: UserRoutesToShow[] | undefined;
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser;
    firstAllowedExternalPathForUser: string | null;
  } => {
    if (
      userType !== UserType.ChamberAdmin &&
      userType !== UserType.ChamberStaff
    ) {
      return {
        navBarConfigToShow: [],
        userRoutesToShow: [],
        firstAllowedPathThatShouldBeConsideredTheHomePageForUser:
          null,
        firstAllowedExternalPathForUser: null,
      };
    }

    if (!userType || userType.length === 0) {
      return {
        navBarConfigToShow: [],
        userRoutesToShow: [],
        firstAllowedPathThatShouldBeConsideredTheHomePageForUser:
          null,
        firstAllowedExternalPathForUser: null,
      };
    }

    const navBarConfig: NavBarConfig[] = [];
    let resRoutes: UserRoutesToShow[] = [];

    const enabledPages =
      userInfo?.usagePermissions?.chamberMemberPro_EnabledPages || [];
    const chamberPerksDashboardPermissions =
      userInfo?.usagePermissions
        ?.chamberPerksAppAdminConsole_EnabledPages || [];
    const access = {
      userType,
      chamberMemberProPermissions: enabledPages,
      chamberPerksDashboardPermissions,
      hasWebsiteBuilderEntitlement: Boolean(
        userInfo?.chamberInfo?.purchasedItems?.includes(
          ItemsPurchasableByChamberEnum.ChamberMemberProWebsiteBuilderOnly
        )
      ),
      hasChamberPerksAppEntitlement: Boolean(
        userInfo?.chamberInfo?.purchasedItems?.includes(
          ItemsPurchasableByChamberEnum.ChamberPerksApp
        )
      ),
    };
    const allowedRouteGroups = userInfo
      ? filterCmpRouteGroups({
          routeGroups: routeGroups[UserType.ChamberAdmin],
          access,
        })
      : [];

    if (userInfo) {
      for (const routeGroup of allowedRouteGroups) {
        if (routeGroup.nestedRoute) {
          for (const route of routeGroup.nestedRoute.routes) {
            if (
              isCmpRouteAllowed({ route, access }) &&
              !route.isExternalLink
            ) {
              const wrappedElement = route.noPageContainer ? (
                maybeWrapWithTestingOverlay(route.path, route.element)
              ) : (
                <PageContainer
                  title={route.title}
                  description={route.description}
                >
                  {maybeWrapWithTestingOverlay(route.path, route.element)}
                </PageContainer>
              );

              resRoutes.push(
                <Route
                  path={route.path}
                  key={route.path}
                  element={wrappedElement}
                />,
              );
            }
          }
        }

        if (routeGroup.route) {
          if (
            isCmpRouteAllowed({ route: routeGroup.route, access }) &&
            !routeGroup.route.isExternalLink
          ) {
            const wrappedElement = routeGroup.route.noPageContainer ? (
              maybeWrapWithTestingOverlay(
                routeGroup.route.path,
                routeGroup.route.element,
              )
            ) : (
              <PageContainer
                title={routeGroup.route.title}
                description={routeGroup.route.description}
              >
                {maybeWrapWithTestingOverlay(
                  routeGroup.route.path,
                  routeGroup.route.element,
                )}
              </PageContainer>
            );

            resRoutes.push(
              <Route
                path={routeGroup.route.path}
                key={routeGroup.route.path}
                element={wrappedElement}
              />,
            );
          }
        }
      }
    }

    const firstAllowedPathThatShouldBeConsideredTheHomePageForUser: FirstAllowedPathThatShouldBeConsideredTheHomePageForUser =
      getPreferredCmpInternalLandingPath({
        allowedPaths: resRoutes.map((route) => route.props.path),
        // Karen Oct 13 2026: the dashboard always opens on the user guide
        // (some staff cannot open Analytics Overview).
        preferredPaths: [
          RoutePaths.chamberAdmin_guide,
          RoutePaths.chamberAdmin_myChamber_analytics,
          RoutePaths.chamberAdmin_myChamber_chamberSettings,
        ],
      });
    const firstAllowedExternalPathForUser =
      allowedRouteGroups
        .flatMap((routeGroup) =>
          routeGroup.nestedRoute
            ? routeGroup.nestedRoute.routes
            : routeGroup.route
              ? [routeGroup.route]
              : []
        )
        .filter((route) => route.isExternalLink)
        .map((route) =>
          getCmpExternalRouteHref({
            destination: route.externalLinkDestination,
            routePath: route.path,
            websiteUrl: userInfo?.chamberInfo?.chamberMemberProWebsiteUrl,
          })
        )
        .find(Boolean) || null;

    return {
      navBarConfigToShow: navBarConfig,
      userRoutesToShow: resRoutes,
      firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
      firstAllowedExternalPathForUser,
    };
  }, [userType, userInfo]);

  return {
    navBarConfigToShow,
    userRoutesToShow,
    firstAllowedPathThatShouldBeConsideredTheHomePageForUser,
    firstAllowedExternalPathForUser,
    userInfoLoading,
    userInfoError,
  };
};
